// static/js/components/ui/ThreadLimitDialog.jsx

/**
 * ThreadLimitDialog Component
 *
 * This component is used to inform users when they have reached their limit of chat threads. 
 * It provides a dialog interface that pops up once the maximum thread count, determined by
 * the `maxThreadsLimit`, has been reached. The dialog explains the limitation specific to the 
 * user's type (e.g., guest, registered) and offers suggestions on how to manage their thread limit.
 * These suggestions include registering for an account to increase the limit or deleting existing threads
 * to free up space.
 *
 * Props:
 * - `isOpen`: Boolean indicating whether the dialog is open.
 * - `onClose`: Function to call when the dialog needs to be closed.
 * - `maxThreadsLimit`: Number representing the maximum number of threads the user can have.
 * - `userType`: String indicating the type of user (e.g., 'guest', 'registered') to tailor the message
 *   and options provided in the dialog.
 *
 * Usage:
 * The component is used within parent components where managing thread limits is required. It relies on
 * the parent component to control its visibility through the `isOpen` prop and to handle closing via 
 * the `onClose` function.
 *
 * Example:
 * <ThreadLimitDialog
 *   isOpen={isDialogOpen}
 *   onClose={handleCloseDialog}
 *   maxThreadsLimit={10}
 *   userType="guest"
 * />
 *
 * This component leverages the AlertDialog component structure from '@/components/ui/alert-dialog' to
 * ensure consistent styling and behavior with other dialogs in the application.
 */

import React from 'react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const ThreadLimitDialog = ({ isOpen, onClose, maxThreadsLimit, userType }) => {
  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Thread Limit Reached</AlertDialogTitle>
          <AlertDialogDescription className="space-y-4">
            <p>You have reached the maximum limit of {maxThreadsLimit} chat threads allowed for {userType} users.</p>
        
            <div className="text-sm text-primary-text/70">
              <p>To create more threads, you can:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                {userType === 'guest' && (
                  <li>Register for a free account to get more threads</li>
                )}
                <li>Delete one of your existing threads to make room for a new one</li>
              </ul>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={onClose} className="bg-primary-button text-black hover:bg-primary-buttonHover">
            I Understand
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ThreadLimitDialog;
